import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import servicesImage from '../assets/images/hpe_servers/hpe-simplivity-380h-server.jpg';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Avatar,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  CloudQueue as CloudQueueIcon,
  Restore as RestoreIcon,
  Backup as BackupIcon,
} from '@mui/icons-material';

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Services: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('services');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);
  
  const services = [
    {
      id: 1,
      slug: 'virtualization',
      title: 'Virtualization Solutions',
      description: 'Optimize your IT resources with enterprise-grade virtualization technology. Consolidate workloads, improve efficiency, and reduce infrastructure costs while maintaining peak performance.',
      icon: <CloudIcon />,
      features: [
        'Scalable virtual infrastructure',
        'Resource optimization & management',
        'Multi-hypervisor support',
        'Performance monitoring & analytics'
      ]
    },
    {
      id: 2,
      slug: 'private-cloud',
      title: 'Private Cloud & Compute',
      description: 'Build a flexible, secure private cloud environment tailored to your business needs. Complete compute, storage, and networking infrastructure with the agility of cloud and the control of on-premises.',
      icon: <CloudQueueIcon />,
      features: [
        'Self-service cloud portal',
        'Elastic compute resources',
        'Hybrid & multi-cloud integration',
        'Infrastructure automation'
      ]
    },
    {
      id: 3,
      slug: 'disaster-recovery',
      title: 'Disaster Recovery',
      description: 'Ensure business continuity with comprehensive disaster recovery solutions. Minimize downtime and data loss with automated failover and rapid recovery capabilities.',
      icon: <RestoreIcon />,
      features: [
        'Automated backup & replication',
        'Fast recovery time objectives (RTO)',
        'Regular DR testing & validation',
        'Multi-site redundancy options'
      ]
    },
    {
      id: 4,
      slug: 'data-backup',
      title: 'Data Backup & Protection',
      description: 'Protect your critical business data with enterprise-class backup and recovery solutions. Automated, reliable, and scalable data protection for complete peace of mind.',
      icon: <BackupIcon />,
      features: [
        'Automated backup scheduling',
        'Incremental & full backups',
        'Long-term data retention',
        'Ransomware protection & recovery'
      ]
    },
  ];

  return (
    <Box id="services" sx={{ py: { xs: 8, md: 16 }, backgroundColor: 'white' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
          <Typography variant="h2" component="h2" gutterBottom>
            Comprehensive IT Infrastructure Solutions
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', fontSize: { xs: '1rem', md: '1.25rem' }, px: { xs: 2, md: 0 }, mb: 4 }}>
            Build resilient, scalable infrastructure with our complete suite of services—spanning virtualization, 
            private cloud, data protection, and disaster recovery for modern enterprises.
          </Typography>
          <img
            src={servicesImage}
            alt="Hyperconverged infrastructure server for enterprise virtualization and private cloud solutions"
            loading="lazy"
            style={{
              width: '100%',
              maxWidth: '900px',
              height: '300px',
              objectFit: 'cover',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          />
        </Box>

        <Grid container spacing={{ xs: 3, md: 5 }}>
          {services.map((service) => (
            <Grid item xs={12} sm={6} md={6} key={service.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  animation: isVisible ? `${slideInUp} 0.6s ease-out ${service.id * 0.1}s both` : 'none',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      mb: { xs: 2, md: 3 },
                      width: { xs: 56, md: 64 },
                      height: { xs: 56, md: 64 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1) rotate(10deg)',
                        boxShadow: '0 8px 20px rgba(25, 118, 210, 0.4)',
                      },
                    }}
                  >
                    {service.icon}
                  </Avatar>
                  
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: { xs: 1.5, md: 2 },
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }}
                  >
                    {service.title}
                  </Typography>
                  
                  <Typography 
                    color="text.secondary" 
                    paragraph
                    sx={{ 
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      lineHeight: 1.6,
                      mb: { xs: 2, md: 3 },
                    }}
                  >
                    {service.description}
                  </Typography>

                  <Box component="ul" sx={{ pl: { xs: 1.5, md: 2 }, mt: { xs: 1.5, md: 2 } }}>
                    {service.features.map((feature, index) => (
                      <Typography
                        key={index}
                        component="li"
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: { xs: '0.8rem', md: '0.875rem' } }}
                      >
                        {feature}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
                
                <CardActions sx={{ p: { xs: 3, md: 4 }, pt: 0 }}>
                  <Button 
                    size="small" 
                    color="primary"
                    onClick={() => {
                      navigate(`/services/${service.slug}`);
                      window.scrollTo(0, 0);
                    }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: { xs: 8, md: 12 } }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            sx={{ 
              py: { xs: 2, md: 2.5 }, 
              px: { xs: 4, md: 6 },
              fontSize: { xs: '1rem', md: '1.1rem' },
              fontWeight: 600,
            }}
          >
            Get Started Today
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Services;